// Préférences : thème, densité, taille de police, appliquées immédiatement et enregistrées.
import { DENSITES, TAILLES_POLICE, THEMES, type Preferences } from '@cc/contrats/ui-preferences';
import { Bandeau, Bouton, Panneau } from '@cc/ui';
import { useId } from 'react';
import { EnTetePage } from '../commun/EnTetePage';
import { heureCourte } from '@cc/ui';
import { usePreferences } from '../preferences/FournisseurPreferences';
import styles from './preferences.module.css';

const LIBELLES: Record<string, string> = {
  jour: 'Jour',
  nuit: 'Nuit',
  systeme: 'Selon l\'appareil',
  confortable: 'Confortable',
  compacte: 'Compacte',
  normale: 'Normale',
  grande: 'Grande',
};

function Choix<K extends 'theme' | 'densite' | 'taille_police'>({
  cle,
  titre,
  aide,
  valeurs,
}: {
  cle: K;
  titre: string;
  aide: string;
  valeurs: readonly Preferences[K][];
}) {
  const { preferences, modifier, enCours } = usePreferences();
  const nom = useId();
  return (
    <fieldset className={styles.groupe}>
      <legend>{titre}</legend>
      <p className={styles.aide}>{aide}</p>
      <div className={styles.options}>
        {valeurs.map((v) => (
          <label key={v} className={styles.option}>
            <input
              type="radio"
              name={nom}
              value={v}
              checked={preferences[cle] === v}
              disabled={enCours}
              onChange={() => void modifier({ [cle]: v } as Partial<Preferences>)}
            />
            <span>{LIBELLES[v] ?? v}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function PagePreferences() {
  const { mode, enregistreLe, erreur, preferences, enregistrerDisposition } = usePreferences();
  const nbDispositions = Object.keys(preferences.dispositions).length;
  return (
    <>
      <EnTetePage
        etiquette="Mon compte"
        titre="Préférences"
        description="Les changements s'appliquent immédiatement et sont enregistrés pour votre compte."
      />
      <div role="status" aria-live="polite" className={styles.etat}>
        {mode === 'serveur' ? (
          <Bandeau type="info">
            Préférences enregistrées sur le serveur : elles vous suivent sur tous vos appareils et dans toutes vos collectivités.
            {enregistreLe ? ` Dernier enregistrement à ${heureCourte(enregistreLe)}.` : ''}
          </Bandeau>
        ) : mode === 'appareil' ? (
          <Bandeau type="attention" titre="Enregistrement sur cet appareil uniquement">
            Le serveur n&apos;expose pas encore l&apos;enregistrement des préférences (route /preferences). Vos choix sont conservés dans ce
            navigateur et appliqués à chaque connexion sur cet appareil, mais pas sur un autre.
            {enregistreLe ? ` Dernier enregistrement à ${heureCourte(enregistreLe)}.` : ''}
          </Bandeau>
        ) : null}
        {erreur ? <Bandeau type="erreur">Enregistrement impossible : {erreur}. Le réglage précédent est rétabli.</Bandeau> : null}
      </div>
      <Panneau titre="Affichage">
        <Choix cle="theme" titre="Thème" aide="Le thème « selon l'appareil » suit le réglage clair ou sombre du système." valeurs={THEMES} />
        <Choix cle="densite" titre="Densité" aide="La densité compacte réduit les espacements des listes et des formulaires." valeurs={DENSITES} />
        <Choix cle="taille_police" titre="Taille du texte" aide="La taille grande porte le texte courant de 14 à 16 pixels." valeurs={TAILLES_POLICE} />
      </Panneau>
      <Panneau titre="Dispositions des tableaux de bord">
        <p>
          {nbDispositions === 0
            ? 'Vos tableaux de bord utilisent la disposition par défaut.'
            : `${nbDispositions} tableau${nbDispositions > 1 ? 'x' : ''} de bord personnalisé${nbDispositions > 1 ? 's' : ''} (ordre ou tuiles masquées).`}
        </p>
        {nbDispositions > 0 ? (
          <div>
            <Bouton
              variante="secondaire"
              onClick={() => {
                for (const t of Object.keys(preferences.dispositions)) void enregistrerDisposition(t, null);
              }}
            >
              Rétablir toutes les dispositions par défaut
            </Bouton>
          </div>
        ) : null}
      </Panneau>
    </>
  );
}
